import { useTranslation } from 'react-i18next';
import { Card, CardContent, Typography, Stack, Chip, Box, Divider } from '@mui/material';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import VaccinesOutlinedIcon from '@mui/icons-material/VaccinesOutlined';
import ChecklistRtlOutlinedIcon from '@mui/icons-material/ChecklistRtlOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { DOC_TYPES } from '@/utils/clientDocs';

const TYPE_META = {
  consultation: { Icon: AssignmentIndOutlinedIcon, color: 'primary.main' },
  treatment_record: { Icon: VaccinesOutlinedIcon, color: 'secondary.main' },
  treatment_plan: { Icon: ChecklistRtlOutlinedIcon, color: 'info.main' },
  follow_up: { Icon: MonitorHeartOutlinedIcon, color: 'success.main' },
  follow_up_schedule: { Icon: EventRepeatOutlinedIcon, color: 'warning.main' },
};

export default function SharedDocCard({ doc, clinicName, dateLabel }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { Icon, color } = TYPE_META[doc.type] || TYPE_META.consultation;
  const schema = DOC_TYPES[doc.type];
  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(lang === 'fa' ? 'fa-IR' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '');

  const renderValue = (field) => {
    const v = doc.values[field.k];
    const empty = Array.isArray(v) ? v.length === 0 : (field.t === 'switch' ? !v : !(v && String(v).trim()));
    if (empty) return null;
    if (field.t === 'chips') {
      return <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>{v.map((x, i) => <Chip key={i} size="small" label={x} />)}</Stack>;
    }
    if (field.t === 'captureSchedule') {
      return (
        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
          {[...v].sort().map((iso) => (
            <Stack key={iso} direction="row" spacing={1} alignItems="center">
              <CalendarMonthIcon fontSize="small" color="action" />
              <Typography variant="body2">{fmtDate(iso)}</Typography>
            </Stack>
          ))}
        </Stack>
      );
    }
    if (field.t === 'switch') return <Chip size="small" color="success" variant="outlined" label={t('docs.on')} sx={{ mt: 0.5 }} />;
    if (field.t === 'date') return <Typography variant="body2">{fmtDate(v)}</Typography>;
    if (field.t === 'select') return <Typography variant="body2">{t(`docs.opt.${field.k}.${v}`, v)}</Typography>;
    return <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{v}</Typography>;
  };

  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.25, bgcolor: 'action.hover' }}>
        <Box sx={{ width: 34, height: 34, borderRadius: 2, display: 'grid', placeItems: 'center', color: '#fff', bgcolor: color, flexShrink: 0 }}>
          <Icon sx={{ fontSize: 19 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={800} noWrap>{t(`docs.types.${doc.type}`, doc.type)}</Typography>
          {dateLabel && <Typography variant="caption" color="text.secondary">{dateLabel}</Typography>}
        </Box>
      </Box>
      <CardContent>
        <Stack spacing={1.25} divider={<Divider flexItem />}>
          {(schema.fields || []).map((f) => {
            const node = renderValue(f);
            if (!node) return null;
            return (
              <Box key={f.k}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block' }}>
                  {t(`docs.fields.${f.k}`, f.k)}
                </Typography>
                {node}
              </Box>
            );
          })}
        </Stack>
        {clinicName && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary', mt: 1.5 }}>
            <LocalHospitalIcon sx={{ fontSize: 14 }} />
            <Typography variant="caption">{clinicName}</Typography>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
